import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { useMacros, type Macro } from '@/hooks/useMacros';
import { MacroDialog } from './MacroDialog';
import { Sparkles, Search, Globe, User, Plus, Edit, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface MacrosManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MacrosManagerDialog({ open, onOpenChange }: MacrosManagerDialogProps) {
  const { globalMacros, individualMacros, isLoading, deleteMacro } = useMacros();
  const [searchQuery, setSearchQuery] = useState('');
  const [showMacroDialog, setShowMacroDialog] = useState(false);
  const [selectedMacro, setSelectedMacro] = useState<Macro | null>(null);
  const [macroDialogMode, setMacroDialogMode] = useState<'create' | 'edit'>('create');
  const [macroToDelete, setMacroToDelete] = useState<Macro | null>(null);

  const filteredGlobalMacros = globalMacros.filter((macro) =>
    macro.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    macro.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredIndividualMacros = individualMacros.filter((macro) =>
    macro.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    macro.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateNew = () => {
    setSelectedMacro(null);
    setMacroDialogMode('create');
    setShowMacroDialog(true);
  };

  const handleEdit = (macro: Macro) => {
    setSelectedMacro(macro);
    setMacroDialogMode('edit');
    setShowMacroDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (macroToDelete) {
      deleteMacro(macroToDelete.id);
      setMacroToDelete(null);
    }
  };

  const renderMacroItem = (macro: Macro) => (
    <div
      key={macro.id}
      className="p-3 border rounded-lg hover:bg-accent/50 transition-colors group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm truncate">{macro.title}</h4>
            {macro.is_global ? (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                Global
              </Badge>
            ) : (
              <Badge variant="outline" className="flex items-center gap-1">
                <User className="h-3 w-3" />
                Pessoal
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">{macro.content}</p>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(macro)}
            className="h-7 w-7 p-0"
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMacroToDelete(macro)}
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[700px] h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Gerenciar Macros
            </DialogTitle>
            <DialogDescription>
              Crie e gerencie mensagens prontas para uso rápido
            </DialogDescription>
          </DialogHeader>

          {/* Search and Create Button */}
          <div className="flex gap-2 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar macros..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Button onClick={handleCreateNew} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Nova Macro
            </Button>
          </div>

          {/* Macros List */}
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-6 pb-4">
              {/* Global Macros */}
              {filteredGlobalMacros.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Macros Globais ({filteredGlobalMacros.length})
                  </h3>
                  <div className="space-y-2">
                    {filteredGlobalMacros.map(renderMacroItem)}
                  </div>
                </div>
              )}

              {/* Individual Macros */}
              {filteredIndividualMacros.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Minhas Macros ({filteredIndividualMacros.length})
                  </h3>
                  <div className="space-y-2">
                    {filteredIndividualMacros.map(renderMacroItem)}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!isLoading &&
                filteredGlobalMacros.length === 0 &&
                filteredIndividualMacros.length === 0 && (
                  <div className="text-center py-12">
                    <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">
                      {searchQuery
                        ? 'Nenhuma macro encontrada'
                        : 'Nenhuma macro cadastrada ainda'}
                    </p>
                    {!searchQuery && (
                      <Button
                        variant="outline"
                        onClick={handleCreateNew}
                        className="mt-4"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Criar Primeira Macro
                      </Button>
                    )}
                  </div>
                )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Macro Create/Edit Dialog */}
      <MacroDialog
        open={showMacroDialog}
        onOpenChange={setShowMacroDialog}
        macro={selectedMacro}
        mode={macroDialogMode}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!macroToDelete} onOpenChange={() => setMacroToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Macro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a macro "{macroToDelete?.title}"?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 w-full">
              Excluir
            </AlertDialogAction>
            <AlertDialogCancel className="w-full">Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
